import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  input,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isDefined } from '@trimble-gcs/common';
import {
  ModusButtonModule,
  ModusFormFieldModule,
  ModusIconModule,
  ModusInputModule,
  ModusSwitchModule,
} from '@trimble-gcs/modus';
import { filter, map } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ColorPickerComponent } from '../../../color-picker/color-picker.component';
import { ColorPickerFormDirective } from '../../../color-picker/color-picker.directive';
import { Scan3dClearanceBand } from '../../models/scan-3d-clearance-band';
import { DEFAULT_SCAN_3D_STYLE, Scan3dStyle } from '../../models/scan-3d-style';
import { Scan3dStyleUom } from '../../models/scan-3d-style-uom';
import { Scan3dLengthInputComponent } from '../scan-3d-length-input/scan-3d-length-input.component';
import { Scan3dLengthInputDirective } from '../scan-3d-length-input/scan-3d-length-input.directive';
import { scan3dClearanceTranslations } from './scan-3d-clearance.translations';

type ClearanceBandForm = FormGroup<{
  // Give the band an ID to trackBy, cannot use $index because it changes based on the start value.
  id: FormControl<string>;
  focus: FormControl<boolean>;
  start: FormControl<number>;
  color: FormControl<string>;
}>;

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-clearance',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModusSwitchModule,
    MatSliderModule,
    MatIconModule,
    ModusIconModule,
    ColorPickerComponent,
    ColorPickerFormDirective,
    ModusFormFieldModule,
    ModusInputModule,
    ModusButtonModule,
    Scan3dLengthInputComponent,
    Scan3dLengthInputDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './scan-3d-clearance.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dClearanceComponent {
  readonly translations = scan3dClearanceTranslations();

  readonly scan3dStyle = input.required<Scan3dStyle>();
  readonly scan3dStyleUom = input.required<Scan3dStyleUom>();

  readonly styleChange = output<Partial<Scan3dStyle>>();

  readonly lengthUnit = computed(() => this.scan3dStyleUom().lengthUnit);

  private readonly numberPattern = /^-?(\d+(\.\d+)?|\.\d+)([eE][+-]?\d+)?$/; // Matches integers, decimals (including .[0-9]), negatives and scientific notation
  private readonly defaultBandColor = '#000000';

  readonly formGroup = new FormGroup({
    defaultTransparent: new FormControl<boolean>(false, { nonNullable: true }),
    defaultColor: new FormControl<string>(DEFAULT_SCAN_3D_STYLE.clearanceDefaultColor, {
      nonNullable: true,
    }),
    bands: new FormArray<ClearanceBandForm>([]),
  });

  get formBands(): FormArray<ClearanceBandForm> {
    return this.formGroup.controls.bands;
  }

  get focusedBandIndex(): number | null {
    const index = this.formBands.controls.findIndex((band) => band.controls.focus.value);
    return index === -1 ? null : index;
  }

  constructor() {
    this.subscribeToScan3dStyle();
    this.subscribeToFormValueChanges();
  }

  removeBand(): void {
    const bands = this.formBands;
    const indexToRemove = this.focusedBandIndex ?? this.formBands.length - 1;

    bands.removeAt(indexToRemove);

    if (bands.length > indexToRemove) {
      this.setFocusedBand(indexToRemove);
    } else {
      this.setFocusedBand(bands.length - 1);
    }
  }

  addBand() {
    const currentIndex = this.focusedBandIndex ?? this.formBands.length - 1;
    const band = this.formBands.at(currentIndex);

    const previousBandStartValue = band?.get('start')?.value ?? 0;
    const previousBandColor = band?.get('color')?.value ?? this.defaultBandColor;
    const bandGroup = this.createBandFormGroup(previousBandStartValue, previousBandColor);

    this.formBands.insert(currentIndex + 1, bandGroup);

    this.setFocusedBand(currentIndex + 1);
  }

  setFocusedBand(index: number | null): void {
    const focusedBand = this.formBands.value.map((band, i) => ({
      focus: i === index,
    }));

    this.formBands.patchValue(focusedBand, { emitEvent: false });
  }

  bandBlurred($event: FocusEvent) {
    this.sortBands();

    // if blur is not caused by add/remove then clear focus
    const relatedTarget = $event.relatedTarget as HTMLElement | null;
    const keepFocus = isDefined(relatedTarget?.attributes.getNamedItem('keep-band-focus'));
    if (keepFocus) return;

    this.setFocusedBand(null);
  }

  sortBands() {
    if (this.formGroup.invalid) return;

    const bands = this.formBands.value ?? [];
    const sortedBands = bands.toSorted((a, b) => (a.start ?? 0) - (b.start ?? 0));
    const sortChanged = bands.some((band, i) => band !== sortedBands[i]);

    if (!sortChanged) return;

    this.formGroup.patchValue({ bands: sortedBands });
  }

  private subscribeToFormValueChanges(): void {
    this.formGroup.valueChanges
      .pipe(
        filter(() => {
          if (!this.formGroup.valid) return false;

          // This is intentionally not a validator because we don't want to show an error,
          // we want to not update state until sort is applied.
          return this.formGroup.controls.bands.value.every((current, i, values) => {
            return i === 0 || Number(values[i - 1].start) <= Number(current.start);
          });
        }),
        map(() => this.getFormValue()),
        // Only emit if the form has changed from the input style values.
        // Prevents unnecessary emits for equivalent changes (e.g. 0 vs 0.0).
        filter((style) => !this.scan3dStylesEqual(style, this.scan3dStyle())),
        untilDestroyed(this),
      )
      .subscribe((partialStyle) => {
        this.styleChange.emit(partialStyle);
      });
  }

  private subscribeToScan3dStyle(): void {
    const scan3dStyle$ = toObservable(this.scan3dStyle).pipe(untilDestroyed(this));

    scan3dStyle$
      .pipe(
        // Only reset the form if the input style has changed from the form values.
        filter((style) => !this.scan3dStylesEqual(style, this.getFormValue())),
      )
      .subscribe((style) => {
        this.updateFormBands(style.clearanceBands);

        this.formGroup.patchValue(
          {
            defaultTransparent: style.clearanceDefaultTransparent,
            defaultColor: style.clearanceDefaultColor,
          },
          { emitEvent: false },
        );
      });

    // set form state
    scan3dStyle$.subscribe((style) => {
      if (style.clearanceDefaultTransparent) {
        this.formGroup.controls.defaultColor.disable({ emitEvent: false });
      } else {
        this.formGroup.controls.defaultColor.enable({ emitEvent: false });
      }
    });
  }

  private updateFormBands(bands: Scan3dClearanceBand[]): void {
    const formBands = this.formBands;
    bands.forEach((band, index) => {
      if (formBands.at(index)) {
        formBands.at(index).patchValue(band, { emitEvent: false });
      } else {
        const bandGroup = this.createBandFormGroup(band.start, band.color);
        formBands.push(bandGroup, { emitEvent: false });
      }
    });

    while (formBands.length > bands.length) {
      formBands.removeAt(formBands.length - 1, { emitEvent: false });
    }
  }

  private createBandFormGroup(startValue: number, color: string): ClearanceBandForm {
    return new FormGroup({
      id: new FormControl<string>(uuidv4(), { nonNullable: true }),
      focus: new FormControl<boolean>(false, { nonNullable: true }),
      start: new FormControl<number>(startValue, {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(this.numberPattern)],
      }),
      color: new FormControl<string>(color, { nonNullable: true }),
    });
  }

  private getFormValue(): Partial<Scan3dStyle> {
    const value = this.formGroup.value;

    // Default color can be undefined because the form control can be disabled
    const clearanceDefaultColor =
      value.defaultColor ??
      this.scan3dStyle()?.clearanceDefaultColor ??
      DEFAULT_SCAN_3D_STYLE.clearanceDefaultColor;

    return {
      clearanceDefaultTransparent: value.defaultTransparent ?? false,
      clearanceDefaultColor,
      clearanceBands:
        value.bands?.map((band) => ({
          start: parseFloat(band.start?.toString() ?? '0'),
          color: band.color ?? this.defaultBandColor,
        })) ?? [],
    } satisfies Partial<Scan3dStyle>;
  }

  private scan3dStylesEqual(a: Partial<Scan3dStyle>, b: Partial<Scan3dStyle>): boolean {
    return (
      a.clearanceDefaultTransparent === b.clearanceDefaultTransparent &&
      a.clearanceDefaultColor === b.clearanceDefaultColor &&
      isDefined(a.clearanceBands) &&
      isDefined(b.clearanceBands) &&
      a.clearanceBands.every(
        (band, i) =>
          band.start === b.clearanceBands?.[i]?.start &&
          band.color === b.clearanceBands?.[i]?.color,
      )
    );
  }
}
